import { createJSONStorage } from "zustand/middleware";
import { createStore } from ".";
import type { AccessProps } from "../../types/auth.types";
import { jwtDecode } from "jwt-decode";

type JwtPayload = {
  exp?: number; // 만료(초, epoch)
  iat?: number;
  sub?: string;
  [k: string]: unknown;
};

const initState = {
  accessToken: "",
  refreshToken: "",
  expiresAt: 0
};

interface AccessState {
  access: AccessProps;
  payload: JwtPayload | null;
  setAccess: (access: AccessProps) => void;
  clearAccess: () => void;
  isAuthenticated: () => boolean;
  setIsRefreshing: (status: boolean) => void;
  isRefreshing: boolean;
  willExpireSoon: (leewaySec?: number) => boolean;
  getAccessToken: () => string | null;
}

export const useAccessStore = createStore<AccessState>(
  (set, get) => ({
    access: initState,
    payload: null,
    isRefreshing: false,
    setAccess: (partial) => {
      const next = { ...get().access, ...partial };
      let payload: JwtPayload | null = null;

      // accessToken이 존재하면 디코딩 시도
      try {
        if (next.accessToken) payload = jwtDecode<JwtPayload>(next.accessToken);
      } catch {
        payload = null;
      }

      set({ access: next, payload });
    },

    clearAccess: () => set({ access: initState, payload: null }),

    isAuthenticated: () => {
      const { accessToken } = get().access;
      const { payload } = get();
      if (!accessToken || !payload?.exp) return false;
      const now = Math.floor(Date.now() / 1000);
      return now < payload.exp;
    },

    willExpireSoon: (leewaySec = 30) => {
      const { payload } = get();
      if (!payload?.exp) return true; // exp 없으면 만료 취급
      const now = Math.floor(Date.now() / 1000);
      return now >= payload.exp - leewaySec;
    },
    setIsRefreshing: (status) => set({ isRefreshing: status }),
    getAccessToken: () => {
      const t = get().access.accessToken;
      return t && t.length > 0 ? t : null;
    },
  }),
  {
    name: "parkgolf-access",
    storage: createJSONStorage(() => localStorage),
  }
);

// 여러 탭 동기화: 다른 탭이 토큰을 갱신/회전하면 localStorage 는 바뀌지만
// 이 탭의 인메모리 스토어는 그대로 남아 옛 refreshToken 으로 갱신을 시도하다
// 실패한다. storage 이벤트로 재-hydrate 해 getState() 가 항상 최신값을 보게 한다.
if (typeof window !== "undefined") {
  window.addEventListener("storage", (e) => {
    if (e.key === "parkgolf-access") {
      useAccessStore.persist.rehydrate();
    }
  });
}
